import { FC, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IClassroomColumn } from '../../../models/classrooms.model';
import { RootState } from '../../../store/rootReducer';
import qs from 'qs';
import { Table, TableColumnsType } from 'antd';


import Loader from '../../../components/Loader';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Pagination from '../../../components/Pagination';
import Button from '../../../components/Button';
import config from '../../../lib/config';
import useAxios from '../../../hooks/useAxios';
import { getClassrooms } from '../../../api/classrooms.api';
import ErrorResponse from '../../../components/ErrorResponse';
import { setCurrentCampus } from '../../../store/auth.slice';
import SelectCampus from '../../../components/SelectCampus';
import useAuth from '../../../hooks/useAuth';
import useUtil from '../../../hooks/useUtil';

const CampusClassroom: FC = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const { data: classrooms, isLoading, error } = useAxios(getClassrooms());
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { isCampusDeactived } = useUtil();
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });

  const [data, setData] = useState<IClassroomColumn[]>([]);
  const [page, setPage] = useState(1);
  const [campusDeactived, setCampusDeactived] = useState(false);
  const [roomName, setRoomName] = useState('');
  const [param, setParam] = useState({});

  useEffect(() => {
    getClassrooms();
  }, []);

  useEffect(() => {
    setCampusDeactived(isCampusDeactived());
  }, [isCampusDeactived]);

  useEffect(() => {
    onSearch();
  }, [currentCampus]);

  useEffect(() => {
    // query to state
    const _param: any = {};

    if (query.page) {
      setPage(Number(query.page));
      _param.page = Number(query.page);
    }
    if (query.campusCode) {
      dispatch(setCurrentCampus(String(query.campusCode)));
      _param.campusCode = String(query.campusCode);
    }
    if (query.roomName) {
      setRoomName(String(query.roomName));
      _param.roomName = String(query.roomName);
    }

    setParam(_param);

    // data filtering
    if (classrooms?.data) {
      let _data: any = [];

      classrooms.data.forEach((campus) => {
        campus.classroom.forEach((room) => {
          _data.push({
            classroomCode: room.classroomCode,
            classroomName: room.classroomName,
            classroomMemo: room.classroomMemo,
            campusCode: campus.campusCode,
            campusName: campus.campusName,
          });
        });
      });

      if (query.campusCode) {
        _data = _data.filter(
          (item: any) => item.campusCode === query.campusCode,
        );
      }
      if (query.roomName) {
        _data = _data.filter(
          (item: any) =>
            item.classroomName.indexOf(String(query.roomName)) !== -1,
        );
      }

      setData(_data);
    }
  }, [location, classrooms]);

  const onSearch = () => {
    const param: any = { page: 1 };

    if (currentCampus) {
      param.campusCode = currentCampus;
    }
    if (roomName) {
      param.roomName = roomName;
    }

    setParam(param);
    history.push(`/campus/classroom?${qs.stringify(param)}`);
  };

  const columns: TableColumnsType<IClassroomColumn> = [
    {
      title: t('campus.classroom.column1'),
      dataIndex: 'classroomCode',
      key: 'classroomCode',
      sorter: (a, b) => a.classroomCode?.localeCompare(b.classroomCode),
    },
    {
      title: t('campus.classroom.column2'),
      dataIndex: 'classroomName',
      key: 'classroomName',
      sorter: (a, b) => a.classroomName?.localeCompare(b.classroomName),
    },
    {
      title: t('campus.classroom.column3'),
      dataIndex: 'classroomMemo',
      key: 'classroomMemo',
      sorter: (a, b) => a.classroomMemo?.localeCompare(b.classroomMemo),
    },
  ];

  return (
    <>
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label>
            <div>
              <SelectCampus />
            </div>
          </li>
          <li>
            <input
              type="text"
              className="form-control"
              placeholder={t('campus.classroom.placeholder1')}
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
            />
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>	
    <div className="content scrollbar">
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <SimpleHeader
        iconFile="icon-campus-big.png"
        parentMenu={t('common.menu.campus')}
        currentMenu={t('campus.classroom.title1')}
        onlyTitle={true}
      />

      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">{t('common.text.count')}: <b>{data?.length}{t('common.text.unit')}</b></div>
        </div>
        <div>
          <Table
            className='comm-table__area mt-0'
            columns={columns}
            dataSource={data || undefined}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  history.push(
                    `/campus/classroom/${
                      record.classroomCode
                    }/edit?${qs.stringify(param)}`,
                  );
                },
              };
            }}
            rowClassName="pointer"
            rowKey="classroomCode"
          />
        </div>
        <div className="comm-bx__between">
          <div></div>
          <div className="common-paging center">
            <Pagination
              count={data?.length || 0}
              page={page}
              param={param}
              path="/campus/classroom"
            />
          </div>
          <div  className="comm-btn__area right">
            <Button
              className="btn-basic pink"
              onClick={() =>
                history.push(`/campus/classroom/create?${qs.stringify(param)}`)
              }
              text={t('business.session.button4')}
              disabled={campusDeactived}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default CampusClassroom;
